import { useMutation } from '@tanstack/react-query';

import {
  createCreditCheckoutSession,
  CreditCheckoutUnavailableError,
} from './billingClient';
import type { CheckoutSession } from './types';

interface UseCreditCheckoutResult {
  buyCredits: () => void;
  isPending: boolean;
  isUnavailable: boolean;
  isError: boolean;
}

/**
 * Mutation hook for purchasing a credit pack via Stripe Checkout.
 *
 * On success, redirects to the Stripe-hosted checkout page in the same tab
 * (matching useBilling.openCheckout). When the backend endpoint is not yet
 * deployed (404) or no credit-pack products are configured (501), the hook
 * surfaces isUnavailable=true so the UI can show "Coming soon" rather than
 * a transient error.
 */
export function useCreditCheckout(orgId: number): UseCreditCheckoutResult {
  const mutation = useMutation({
    mutationFn: async () => {
      if (!orgId) {
        throw new Error('Missing orgId');
      }
      return createCreditCheckoutSession(orgId);
    },
    onSuccess: (session: CheckoutSession) => {
      window.location.assign(session.url);
    },
  });

  const isUnavailable =
    mutation.error instanceof CreditCheckoutUnavailableError;
  const isError = mutation.isError && !isUnavailable;

  return {
    buyCredits: mutation.mutate,
    isPending: mutation.isPending,
    isUnavailable,
    isError,
  };
}
